import {
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@lumanity/ui/primitives";
import { ChipsInput } from "@lumanity/ui/projects";
import { cn } from "@/lib/utils";
import { AI_POLICY_LABELS, FUNDING_LABELS, SUPPLEMENTARY_LABELS, parseCount } from "./model";
import type { Limit, LimitUnit, OutletGuidelines } from "./types";

const NOT_SET = "__not_set__";

interface OutletGuidelinesFormProps {
  value: OutletGuidelines;
  onChange: (value: OutletGuidelines) => void;
  disabled?: boolean;
  /** Prefix for input ids, so two forms on a page don't clash. */
  idPrefix?: string;
  className?: string;
}

/**
 * A journal's or congress's submission rules (FR-4-03..05). Used prefilled on
 * an output's Target Outlet step, and by admins to maintain the outlets.
 */
const OutletGuidelinesForm = ({ value, onChange, disabled, idPrefix = "guidelines", className }: OutletGuidelinesFormProps) => {
  const set = <K extends keyof OutletGuidelines>(key: K, v: OutletGuidelines[K]) => onChange({ ...value, [key]: v });
  const id = (name: string) => `${idPrefix}-${name}`;

  return (
    <div className={cn("space-y-5", className)}>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="AI policy" htmlFor={id("ai-policy")}>
          <EnumSelect
            id={id("ai-policy")}
            labels={AI_POLICY_LABELS}
            value={value.ai_policy}
            onChange={(v) => set("ai_policy", v)}
            disabled={disabled}
          />
        </Field>
        <Field label="Keywords" htmlFor={id("keywords")} hint="Maximum number">
          <CountInput id={id("keywords")} value={value.keywords_max} onChange={(v) => set("keywords_max", v)} disabled={disabled} />
        </Field>
        <Field label="Funding" htmlFor={id("funding")}>
          <EnumSelect id={id("funding")} labels={FUNDING_LABELS} value={value.funding} onChange={(v) => set("funding", v)} disabled={disabled} />
        </Field>
        <Field label="Deadline" htmlFor={id("deadline")}>
          <Input
            id={id("deadline")}
            type="date"
            value={value.deadline ?? ""}
            onChange={(e) => set("deadline", e.target.value || null)}
            disabled={disabled}
          />
        </Field>
        <Field label="Audience" htmlFor={id("audience")}>
          <Input
            id={id("audience")}
            value={value.audience ?? ""}
            placeholder="e.g. Oncologists"
            onChange={(e) => set("audience", e.target.value === "" ? null : e.target.value)}
            disabled={disabled}
          />
        </Field>
        <Field label="Supplementary material" htmlFor={id("supplementary")}>
          <EnumSelect
            id={id("supplementary")}
            labels={SUPPLEMENTARY_LABELS}
            value={value.supplementary_material}
            onChange={(v) => set("supplementary_material", v)}
            disabled={disabled}
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title limit" htmlFor={id("title-limit")}>
          <LimitInput id={id("title-limit")} value={value.title_limit} onChange={(v) => set("title_limit", v)} disabled={disabled} />
        </Field>
        <Field label="Word/character limit" htmlFor={id("body-limit")} hint="Body text only">
          <LimitInput id={id("body-limit")} value={value.body_limit} onChange={(v) => set("body_limit", v)} disabled={disabled} />
        </Field>
        <Field label="Graphic limit" htmlFor={id("graphic-limit")}>
          <CountInput
            id={id("graphic-limit")}
            value={value.graphic_limit}
            onChange={(v) => set("graphic_limit", v)}
            suffix="figures/tables"
            disabled={disabled}
          />
        </Field>
        <Field label="Reference limit" htmlFor={id("reference-limit")}>
          <CountInput
            id={id("reference-limit")}
            value={value.reference_limit}
            onChange={(v) => set("reference_limit", v)}
            suffix="refs"
            disabled={disabled}
          />
        </Field>
      </div>

      <Field label="Required sections" htmlFor={id("sections")} hint="Press Enter to add a section">
        <ChipsInput
          id={id("sections")}
          value={value.required_sections}
          onChange={(v) => set("required_sections", v)}
          placeholder="e.g. Background, Methods, Results, Conclusions"
          aria-label="Required sections"
          disabled={disabled}
        />
      </Field>
    </div>
  );
};

const Field = ({ label, htmlFor, hint, children }: { label: string; htmlFor: string; hint?: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <Label htmlFor={htmlFor} className="text-xs font-semibold text-brand-navy">
      {label}
      {hint && <span className="ml-1.5 font-normal text-muted-foreground">({hint})</span>}
    </Label>
    {children}
  </div>
);

function EnumSelect<T extends string>({
  id,
  labels,
  value,
  onChange,
  disabled,
}: {
  id: string;
  labels: Record<T, string>;
  value: T | null;
  onChange: (value: T | null) => void;
  disabled?: boolean;
}) {
  return (
    <Select value={value ?? NOT_SET} onValueChange={(v) => onChange(v === NOT_SET ? null : (v as T))} disabled={disabled}>
      <SelectTrigger id={id}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NOT_SET}>Not specified</SelectItem>
        {(Object.keys(labels) as T[]).map((key) => (
          <SelectItem key={key} value={key}>
            {labels[key]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const CountInput = ({
  id,
  value,
  onChange,
  suffix,
  disabled,
}: {
  id: string;
  value: number | null;
  onChange: (value: number | null) => void;
  suffix?: string;
  disabled?: boolean;
}) => (
  <div className="flex items-center gap-2">
    <Input
      id={id}
      type="number"
      min={0}
      step={1}
      inputMode="numeric"
      className="w-28"
      value={value ?? ""}
      onChange={(e) => onChange(parseCount(e.target.value))}
      disabled={disabled}
    />
    {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
  </div>
);

/** Number + Words/Characters + "With spaces" (only meaningful for characters). Empty number = no limit. */
const LimitInput = ({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: Limit | null;
  onChange: (value: Limit | null) => void;
  disabled?: boolean;
}) => {
  const unit: LimitUnit = value?.unit ?? "words";
  const withSpaces = value?.with_spaces ?? false;
  const update = (patch: Partial<Limit>) => {
    if (!value) return; // unit / spaces only stick once there's a number
    onChange({ ...value, ...patch });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        id={id}
        type="number"
        min={0}
        step={1}
        inputMode="numeric"
        className="w-28"
        value={value?.value ?? ""}
        onChange={(e) => {
          const n = parseCount(e.target.value);
          onChange(n == null ? null : { value: n, unit, with_spaces: withSpaces });
        }}
        disabled={disabled}
      />
      <Select value={unit} onValueChange={(v) => update({ unit: v as LimitUnit })} disabled={disabled || !value}>
        <SelectTrigger className="w-32" aria-label="Limit unit">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="words">Words</SelectItem>
          <SelectItem value="characters">Characters</SelectItem>
        </SelectContent>
      </Select>
      <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Checkbox
          checked={withSpaces}
          onCheckedChange={(checked) => update({ with_spaces: checked === true })}
          disabled={disabled || !value || unit !== "characters"}
          aria-label="With spaces"
        />
        With spaces
      </label>
    </div>
  );
};

export default OutletGuidelinesForm;
